import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ExternalLink, Plus, Trash2, Music, AlertCircle, GripVertical, Search, X, Check, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { openExternalLink } from '../utils/openExternalLink';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Componente sortable para cada canción - DISEÑO MEJORADO
const SortableSongItem = ({ song, id, isProcessing, onAddToLibrary, onAddToPending, onRemove, onTogglePlayed, isDragOverlay = false }) => {
  const [showActions, setShowActions] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    zIndex: isDragging ? 999 : 'auto',
  };

  return (
    <div
      ref={!isDragOverlay ? setNodeRef : undefined}
      style={!isDragOverlay ? style : undefined}
      className={`group transition-all ${
        isDragging && !isDragOverlay ? 'bg-slate-700/30' : ''
      } ${isDragOverlay ? 'bg-slate-700 shadow-xl ring-2 ring-cyan-500 rounded-lg' : ''} ${
        song.played ? 'bg-green-900/10' : 'hover:bg-slate-700/30'
      }`}
    >
      {/* Main Row */}
      <div className="p-3 flex items-center gap-2">
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-slate-600 hover:text-slate-400 p-1 flex-shrink-0 touch-none"
          title="Arrastra para reordenar"
        >
          <GripVertical className="w-4 h-4" />
        </div>

        {/* Song Number */}
        <span className={`text-xs w-6 text-center flex-shrink-0 font-mono ${song.played ? 'text-green-500' : 'text-slate-500'}`}>
          {song.displayIndex}
        </span>

        {/* Played checkbox */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onTogglePlayed?.();
          }}
          className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
            song.played 
              ? 'bg-green-600 border-green-600 text-white' 
              : 'border-slate-500 hover:border-green-500 text-transparent hover:text-green-500'
          }`}
          title={song.played ? 'Marcar como pendiente' : 'Marcar como reproducida'}
        >
          <Check className="w-4 h-4" />
        </button>

        {/* Song Info - ÁREA PRINCIPAL MÁS GRANDE */}
        <div className="flex-1 min-w-0 ml-1">
          <p className={`font-medium text-base leading-tight ${
            song.played ? 'text-green-300/70 line-through' : 'text-white'
          }`}>
            {song.title}
          </p>
          <p className={`text-sm mt-0.5 ${song.played ? 'text-green-400/50' : 'text-slate-400'}`}>
            {song.artist}
          </p>
        </div>

        {/* Quick Status Indicator */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {song.externallyPlayed && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-600/30 text-cyan-300 whitespace-nowrap"
              title="Marcada automáticamente porque está reproducida en tu lista personalizada"
            >
              ↪ lista
            </span>
          )}
          {song.played && (
            <span className="text-green-400 text-xs font-medium">✓</span>
          )}
          {song.in_library && !song.played && (
            <span className="text-cyan-400 text-xs">📚</span>
          )}
          
          {/* Spotify Link - Always visible */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              openExternalLink(song.spotify_url);
            }}
            className="p-2 rounded-full bg-green-600/20 hover:bg-green-600/40 transition-colors text-green-400"
            title="Abrir en Spotify"
          >
            <ExternalLink className="w-4 h-4" />
          </button>

          {/* Toggle actions button */}
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-2 rounded-full bg-slate-700 hover:bg-slate-600 transition-colors text-slate-300 sm:hidden"
          >
            {showActions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {/* Desktop Actions - Hidden on mobile */}
          <div className="hidden sm:flex items-center gap-1">
            {/* Add to Spotify Queue */}
            {song.spotify_uri && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.addToSpotifyQueue) {
                    window.addToSpotifyQueue(song.spotify_uri, song.title, song.artist);
                  }
                }}
                className="p-2 rounded bg-purple-600/20 hover:bg-purple-600/40 transition-colors text-purple-400"
                title="Añadir a cola de Spotify"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </button>
            )}

            {/* Add to Library */}
            {!song.in_library && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddToLibrary?.();
                }}
                disabled={isProcessing}
                className="p-2 rounded bg-cyan-600/20 hover:bg-cyan-600/40 transition-colors text-cyan-400 disabled:opacity-50"
                title="Añadir a biblioteca"
              >
                <Plus className="w-4 h-4" />
              </button>
            )}

            {/* Add to Denon Pending */}
            {!song.in_library && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddToPending?.();
                }}
                className="p-2 rounded bg-orange-600/20 hover:bg-orange-600/40 transition-colors text-orange-400"
                title="Añadir a pendientes Denon"
              >
                <Music className="w-4 h-4" />
              </button>
            )}

            {/* Remove */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove?.();
              }}
              className="p-2 rounded bg-red-600/10 hover:bg-red-600/30 transition-colors text-red-400 opacity-50 hover:opacity-100"
              title="Ocultar de la lista"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Actions Row - Collapsible */}
      {showActions && (
        <div className="px-3 pb-3 flex items-center gap-2 sm:hidden border-t border-slate-700/50 pt-2 mt-1">
          {song.spotify_uri && (
            <button
              onClick={() => window.addToSpotifyQueue?.(song.spotify_uri, song.title, song.artist)}
              className="flex-1 py-2 rounded bg-purple-600/20 text-purple-400 text-xs font-medium"
            >
              Cola Spotify
            </button>
          )}
          {!song.in_library && (
            <>
              <button
                onClick={() => onAddToLibrary?.()}
                disabled={isProcessing}
                className="flex-1 py-2 rounded bg-cyan-600/20 text-cyan-400 text-xs font-medium disabled:opacity-50"
              >
                + Biblioteca
              </button>
              <button
                onClick={() => onAddToPending?.()}
                className="flex-1 py-2 rounded bg-orange-600/20 text-orange-400 text-xs font-medium"
              >
                Denon
              </button>
            </>
          )}
          <button
            onClick={() => onRemove?.()}
            className="py-2 px-3 rounded bg-red-600/20 text-red-400 text-xs"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

const ClientSpotifyPlaylists = ({ playlistId, onSongAdded, externalPlayedKeys }) => {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingIds, setProcessingIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState('all');
  const [activeId, setActiveId] = useState(null);
  const [activeSong, setActiveSong] = useState(null);
  const [collapsedPlaylists, setCollapsedPlaylists] = useState({});

  // Reflejo desde la lista personalizada: una canción del cliente se considera
  // "reproducida" también si la misma canción está marcada en la lista
  // personalizada (emparejando por Spotify ID y, si no, por título+artista).
  const _norm = (s) => (s || '').toString().toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ').trim();
  const isExternallyPlayed = (song) => {
    if (!externalPlayedKeys || externalPlayedKeys.size === 0) return false;
    if (song.spotify_id && externalPlayedKeys.has('sid:' + song.spotify_id)) return true;
    if (song.title && externalPlayedKeys.has('ta:' + _norm(song.title) + '|' + _norm(song.artist))) return true;
    return false;
  };
  // Played efectivo = marcado propio O reflejado desde la lista personalizada
  const effPlayed = (song) => song.played || isExternallyPlayed(song);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchPlaylistsSongs();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: fetchPlaylistsSongs
  }, [playlistId]);

  const fetchPlaylistsSongs = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Add refresh parameter to force reload from Spotify
      const url = forceRefresh 
        ? `${API}/api/playlists/${playlistId}/client-spotify-songs?refresh=true`
        : `${API}/api/playlists/${playlistId}/client-spotify-songs`;
      
      const response = await axios.get(url, { headers });
      
      if (response.data.success) {
        const playlistsWithPlayed = response.data.playlists.map(playlist => ({
          ...playlist,
          songs: playlist.songs.map(song => ({
            ...song,
            played: song.played || false
          }))
        }));
        setPlaylists(playlistsWithPlayed);
        
        if (forceRefresh) {
          toast.success('Playlist actualizada desde Spotify');
        }
      }
    } catch (err) {
      console.error('Error fetching Spotify playlists:', err);
      setError(err.response?.data?.detail || 'Error al cargar las playlists de Spotify');
      toast.error('Error al cargar las playlists de Spotify');
    } finally {
      setLoading(false);
    }
  };

  // Refresh from Spotify (force reload)
  const handleRefreshFromSpotify = () => {
    fetchPlaylistsSongs(true);
  };

  const getFilteredSongs = (songs) => {
    let filtered = [...songs];
    
    if (filterMode === 'played') {
      filtered = filtered.filter(s => effPlayed(s));
    } else if (filterMode === 'pending') {
      filtered = filtered.filter(s => !effPlayed(s));
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s => 
        s.title?.toLowerCase().includes(query) ||
        s.artist?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  const getPlaylistStats = (playlist) => {
    const total = playlist.songs.length;
    const played = playlist.songs.filter(s => effPlayed(s)).length;
    const pending = total - played;
    const percent = total > 0 ? Math.round((played / total) * 100) : 0;
    return { total, played, pending, percent };
  };

  const handleTogglePlayed = async (playlistIndex, songIndex) => {
    const song = playlists[playlistIndex]?.songs[songIndex];
    if (!song) return;
    
    const newPlayedValue = !song.played;
    
    setPlaylists(prev => {
      const newPlaylists = [...prev];
      newPlaylists[playlistIndex].songs[songIndex].played = newPlayedValue;
      return newPlaylists;
    });

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/playlists/${playlistId}/client-spotify-played`,
        { 
          playlistIndex, 
          spotifyId: song.spotify_id,  // Use spotify_id instead of index
          played: newPlayedValue 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error('Error saving played status:', err);
    }
  };

  const handleAddToLibrary = async (song, playlistIndex, songIndex) => {
    const uniqueId = `${playlistIndex}-${songIndex}`;
    setProcessingIds(prev => new Set(prev).add(uniqueId));

    try {
      const response = await axios.post(`${API}/api/songs`, {
        title: song.title,
        artist: song.artist,
        spotify_id: song.spotify_id,
        spotify_url: song.spotify_url,
        spotify_uri: song.spotify_uri,
        rating: 0,
        favorito: false
      });

      if (response.data) {
        setPlaylists(prev => {
          const newPlaylists = [...prev];
          newPlaylists[playlistIndex].songs[songIndex] = {
            ...song,
            in_library: true,
            library_song_id: response.data.id
          };
          newPlaylists[playlistIndex].in_library_count += 1;
          return newPlaylists;
        });

        toast.success(`"${song.title}" añadida a la biblioteca`);
        onSongAdded?.(response.data);
      }
    } catch (err) {
      console.error('Error adding song to library:', err);
      toast.error(err.response?.data?.detail || 'Error al añadir canción');
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(uniqueId);
        return newSet;
      });
    }
  };

  const handleAddToPending = async (song, playlistIndex, songIndex) => {
    // Patrón: el backend no tiene endpoint dedicado "denon pending";
    // lo que se hace es crear la canción en la biblioteca con el flag
    // `pendiente_denon: true` (mismo patrón que App.js líneas ~7525).
    // Si ya estuviera en la biblioteca este botón no se muestra (UI guard).
    try {
      const response = await axios.post(`${API}/api/songs`, {
        title: song.title,
        artist: song.artist,
        spotify_id: song.spotify_id,
        spotify_url: song.spotify_url,
        spotify_uri: song.spotify_uri,
        genres: song.genres || [],
        rating: 0,
        favorito: false,
        pendiente_denon: true,
      });

      if (response.data) {
        setPlaylists(prev => {
          const newPlaylists = [...prev];
          newPlaylists[playlistIndex].songs[songIndex] = {
            ...song,
            in_library: true,
            library_song_id: response.data.id,
          };
          newPlaylists[playlistIndex].in_library_count += 1;
          return newPlaylists;
        });
        onSongAdded?.(response.data);
      }

      toast.success(`"${song.title}" añadida a pendientes Denon`);
    } catch (err) {
      console.error('Error adding to Denon pending:', err);
      toast.error(err.response?.data?.detail || 'Error al añadir a pendientes');
    }
  };

  const handleRemoveFromView = (playlistIndex, songIndex) => {
    setPlaylists(prev => {
      const newPlaylists = [...prev];
      const removedSong = newPlaylists[playlistIndex].songs[songIndex];
      newPlaylists[playlistIndex].songs.splice(songIndex, 1);
      newPlaylists[playlistIndex].total_songs -= 1;
      if (removedSong.in_library) {
        newPlaylists[playlistIndex].in_library_count -= 1;
      }
      return newPlaylists;
    });
    toast.success('Canción ocultada');
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    const [pIdx, sIdx] = active.id.split('-').map(Number);
    if (playlists[pIdx]?.songs[sIdx]) {
      setActiveSong({ ...playlists[pIdx].songs[sIdx], displayIndex: sIdx + 1 });
    }
  };

  const handleDragEnd = async (event, playlistIndex) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveSong(null);

    if (!over || active.id === over.id) return;

    const oldIndex = parseInt(active.id.split('-')[1]);
    const newIndex = parseInt(over.id.split('-')[1]);

    if (oldIndex === newIndex) return;

    // Get the reordered songs
    const reorderedSongs = arrayMove([...playlists[playlistIndex].songs], oldIndex, newIndex);
    
    // Update local state
    setPlaylists(prev => {
      const newPlaylists = [...prev];
      newPlaylists[playlistIndex].songs = reorderedSongs;
      newPlaylists[playlistIndex].has_custom_order = true;
      return newPlaylists;
    });

    toast.success('Orden guardado');

    // Save complete order to backend (by spotify_id for robustness)
    try {
      const token = localStorage.getItem('token');
      const songOrder = reorderedSongs.map(song => song.spotify_id);
      
      await axios.put(
        `${API}/api/playlists/${playlistId}/client-spotify-reorder`,
        { playlistIndex, songOrder },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error('Error saving order:', err);
      toast.error('Error al guardar el orden');
    }
  };

  const handleMarkAll = (playlistIndex, markAsPlayed) => {
    setPlaylists(prev => {
      const newPlaylists = [...prev];
      newPlaylists[playlistIndex].songs = newPlaylists[playlistIndex].songs.map(song => ({
        ...song,
        played: markAsPlayed
      }));
      return newPlaylists;
    });
    toast.success(markAsPlayed ? 'Todas marcadas como reproducidas' : 'Todas desmarcadas');
  };

  const togglePlaylistCollapse = (index) => {
    setCollapsedPlaylists(prev => ({ ...prev, [index]: !prev[index] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Cargando playlists de Spotify...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{error}</p>
          <Button onClick={fetchPlaylistsSongs} className="bg-cyan-600 hover:bg-cyan-700">
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  if (playlists.length === 0) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <Music className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400 text-lg mb-2">No hay playlists de Spotify configuradas</p>
          <p className="text-slate-500 text-sm">
            El cliente puede añadirlas desde su portal
          </p>
        </div>
      </div>
    );
  }

  const totalStats = playlists.reduce((acc, p) => {
    const stats = getPlaylistStats(p);
    return {
      total: acc.total + stats.total,
      played: acc.played + stats.played,
      pending: acc.pending + stats.pending
    };
  }, { total: 0, played: 0, pending: 0 });

  return (
    <div className="space-y-4">
      {/* Header compacto */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              🎵 Playlists del Cliente
            </h2>
            <p className="text-slate-400 text-sm mt-1">
              {totalStats.total} canciones · 
              <span className="text-green-400 ml-1">{totalStats.played} reproducidas</span> · 
              <span className="text-amber-400 ml-1">{totalStats.pending} pendientes</span>
            </p>
          </div>
          
          {/* Progress bar + Refresh button */}
          <div className="flex items-center gap-3">
            <div className="w-full sm:w-48">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-400">Progreso</span>
                <span className="text-green-400">{totalStats.total > 0 ? Math.round((totalStats.played / totalStats.total) * 100) : 0}%</span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-green-600 to-green-400 transition-all duration-300"
                  style={{ width: `${totalStats.total > 0 ? (totalStats.played / totalStats.total) * 100 : 0}%` }}
                />
              </div>
            </div>
            
            {/* Refresh from Spotify button */}
            <button
              onClick={handleRefreshFromSpotify}
              disabled={loading}
              className="p-2 rounded-lg bg-green-600/20 text-green-400 hover:bg-green-600/40 transition-colors disabled:opacity-50 flex-shrink-0"
              title="Actualizar desde Spotify (obtener cambios del cliente)"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Buscar canción o artista..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-900 border-slate-600 text-white placeholder-slate-500 h-9"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex gap-1">
            {[
              { key: 'all', label: 'Todas', color: 'cyan' },
              { key: 'pending', label: 'Pendientes', color: 'amber' },
              { key: 'played', label: 'Reproducidas', color: 'green' },
            ].map(({ key, label, color }) => (
              <Button
                key={key}
                variant={filterMode === key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterMode(key)}
                className={`text-xs h-9 ${
                  filterMode === key 
                    ? `bg-${color}-600 hover:bg-${color}-700 border-${color}-600` 
                    : 'border-slate-600 text-slate-400 hover:text-white'
                }`}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Playlists */}
      {playlists.map((playlist, playlistIndex) => {
        const filteredSongs = getFilteredSongs(playlist.songs);
        const stats = getPlaylistStats(playlist);
        const isCollapsed = collapsedPlaylists[playlistIndex];
        const sortableIds = playlist.songs.map((_, idx) => `${playlistIndex}-${idx}`);

        return (
          <div key={playlistIndex} className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
            {/* Playlist Header - Clickable */}
            <button
              onClick={() => togglePlaylistCollapse(playlistIndex)}
              className="w-full p-3 bg-slate-900 flex items-center justify-between hover:bg-slate-800/80 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="bg-cyan-600/30 text-cyan-300 px-2 py-0.5 rounded text-sm font-medium whitespace-nowrap">
                  {playlist.label}
                </span>
                <span className="text-white font-medium truncate">
                  {playlist.spotify_name}
                </span>
              </div>
              
              <div className="flex items-center gap-3 flex-shrink-0">
                {/* Custom order indicator */}
                {playlist.has_custom_order && (
                  <span className="text-xs bg-purple-600/30 text-purple-300 px-2 py-0.5 rounded hidden sm:inline" title="Orden personalizado guardado">
                    📌 Personalizado
                  </span>
                )}
                
                <div className="hidden sm:flex items-center gap-2 text-xs">
                  <span className="text-green-400">{stats.played}✓</span>
                  <span className="text-slate-500">/</span>
                  <span className="text-slate-400">{stats.total}</span>
                </div>
                
                {/* Mini progress */}
                <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500"
                    style={{ width: `${stats.percent}%` }}
                  />
                </div>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openExternalLink(playlist.spotify_url);
                  }}
                  className="p-1.5 rounded bg-green-600/20 text-green-400 hover:bg-green-600/40"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
                
                {isCollapsed ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronUp className="w-5 h-5 text-slate-400" />}
              </div>
            </button>

            {/* Songs List */}
            {!isCollapsed && (
              <>
                {/* Quick Actions */}
                <div className="px-3 py-2 bg-slate-800/50 border-b border-slate-700 flex items-center gap-2">
                  <span className="text-xs text-slate-500">Marcar:</span>
                  <button
                    onClick={() => handleMarkAll(playlistIndex, true)}
                    className="text-xs px-2 py-1 rounded bg-green-600/20 text-green-400 hover:bg-green-600/40"
                  >
                    ✓ Todas
                  </button>
                  <button
                    onClick={() => handleMarkAll(playlistIndex, false)}
                    className="text-xs px-2 py-1 rounded bg-slate-700 text-slate-400 hover:bg-slate-600"
                  >
                    Ninguna
                  </button>
                </div>

                {filteredSongs.length === 0 ? (
                  <div className="p-6 text-center text-slate-500 text-sm">
                    {searchQuery || filterMode !== 'all' 
                      ? 'No hay canciones con estos filtros'
                      : 'Playlist vacía'}
                  </div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={(event) => handleDragEnd(event, playlistIndex)}
                    onDragCancel={() => { setActiveId(null); setActiveSong(null); }}
                  >
                    <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                      <div className="divide-y divide-slate-700/50">
                        {playlist.songs.map((song, songIndex) => {
                          const uniqueId = `${playlistIndex}-${songIndex}`;
                          const isFiltered = !filteredSongs.includes(song);
                          if (isFiltered && (searchQuery || filterMode !== 'all')) return null;

                          return (
                            <SortableSongItem
                              key={uniqueId}
                              id={uniqueId}
                              song={{ ...song, played: effPlayed(song), externallyPlayed: !song.played && isExternallyPlayed(song), displayIndex: songIndex + 1 }}
                              isProcessing={processingIds.has(uniqueId)}
                              onAddToLibrary={() => handleAddToLibrary(song, playlistIndex, songIndex)}
                              onAddToPending={() => handleAddToPending(song, playlistIndex, songIndex)}
                              onRemove={() => handleRemoveFromView(playlistIndex, songIndex)}
                              onTogglePlayed={() => handleTogglePlayed(playlistIndex, songIndex)}
                            />
                          );
                        })}
                      </div>
                    </SortableContext>

                    <DragOverlay>
                      {activeId && activeSong ? (
                        <SortableSongItem
                          id={activeId}
                          song={activeSong}
                          isProcessing={false}
                          isDragOverlay={true}
                        />
                      ) : null}
                    </DragOverlay>
                  </DndContext>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ClientSpotifyPlaylists;
